import {AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, Renderer2} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {InfiniteScrollModule} from 'ngx-infinite-scroll';

import {Listing} from '../../../../core/models/listing';
import {TorontoListingsService} from '../../services/toronto-listings.service';
import {SEOService} from '../../../../core/services/seo.service';
import {NavigationComponent} from '../../../../shared/components/navigation/navigation.component';
import {TorontoListingsSearchComponent} from '../search-filter/toronto-listings-search.component';
import {BaseDashboardComponent} from "../../../../shared/components/base-dashboard/base-dashboard.component";
import {LISTINGS_SERVICE_TOKEN} from "../../../../core/services/listings-service.token";


import {Globals} from '../../../../core/services/globals';

import {BsModalService} from 'ngx-bootstrap/modal';

import {TrackingService} from '../../../../core/services/tracking.service';
// GA4 Analytics Tracking
import {DataLayerService} from '../../../../core/services/data-layer.service';

import {NgbdCarouselConfigComponent} from '../../../../shared/components/carousel/carousel-config';
import {CommonModule, CurrencyPipe, DOCUMENT} from '@angular/common';
import {BreakpointObserver} from '@angular/cdk/layout';
import {DomSanitizer} from "@angular/platform-browser";
import {PropertyCardComponent} from "../../../../shared/components/property-card/property-card-component";

@Component({
  imports: [
    CommonModule,
    InfiniteScrollModule,
    NavigationComponent,
    RouterModule,
    NgbdCarouselConfigComponent,
    TorontoListingsSearchComponent,
    PropertyCardComponent
  ],
  providers: [
    CurrencyPipe,  // <-- Add this if needed
    {provide: LISTINGS_SERVICE_TOKEN, useClass: TorontoListingsService}
  ],
  standalone: true,
  selector: 'my-toronto-dashboard',
  templateUrl: './toronto-dashboard.component.html',
  styleUrls: ['./toronto-dashboard.component.css'],
})

export class TorontoDashboardComponent extends BaseDashboardComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(
    protected override router: Router,
    protected override activatedRoute: ActivatedRoute,
    protected override http: HttpClient,
    protected override renderer: Renderer2,
    protected override listingsService: TorontoListingsService,
    protected override SEOService: SEOService,
    public override globals: Globals,
    protected override elementRef: ElementRef,
    protected override modalService: BsModalService,
    protected override dataLayerService: DataLayerService,
    protected override trackingService: TrackingService,
    protected override breakpointObserver: BreakpointObserver,
    protected override sanitizer: DomSanitizer,
    @Inject(DOCUMENT) protected override document: Document,
    protected override zone: NgZone,
    @Inject(PLATFORM_ID) protected override platformId: Object,
  ) {
    super(
      router,
      activatedRoute,
      http,
      renderer,
      listingsService,
      SEOService,
      globals,
      elementRef,
      modalService,
      dataLayerService,
      trackingService,
      breakpointObserver,
      sanitizer,
      document,
      zone,
      platformId
    );
  }

  ngOnInit(): void {
    console.log('ngOnInit: Dashboard');
    // Initialize the base dashboard functionality
    this.initializeBaseDashboard();

    // override the registration status to not require registration
    this.globals.isRegisteredBln = '1';

    // set the community to Toronto if not already set
    this.globals.districtList = 'Toronto';

    // Detect screen changes
    this.setupBreakpointObserver();

    // Perform shared initialization tasks
    this.initializeSharedData();

    // load the initial page data
    this.initializePageContent();

    // Allow child classes to customize their behavior
    this.initDashboard();
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit: Dashboard');

    // Run the scroll restoration outside Angular's zone
    this.zone.runOutsideAngular(() => {
      // Use a minimal timeout to ensure the view is stable
      setTimeout(() => {
        if (this.globals.previousRouteStr.includes('/fulldetails/')) {
          this.setScrollTo('resultsList').then(() => {
            // Run change detection inside Angular zone after the scroll is complete
            this.zone.run(() => {
            });
          });
        }

        // Run initialization inside Angular zone
        this.zone.run(() => {
          this.initializeAfterViewInit();
        });
      }, 0);
    });
  }


  ngOnDestroy(): void {
    this.cleanupBaseDashboard();

    this.destroyed$.next();
    this.destroyed$.complete();
  }


  protected override initDashboard(): void {
    const currentRoute = this.router.url;
    console.log('init Peel Dashboard');
    console.log('Previous Route: ' + this.globals.previousRouteStr);

    // Check if returning from details and from the same region
    const isReturningFromDetails = this.globals.previousRouteStr.includes('/fulldetails/');
    const currentRegion = this.getRegionFromRoute(currentRoute);
    const previousRegion = this.getRegionFromRoute(this.globals.previousRouteStr);

    console.log('isReturningFromDetails:', isReturningFromDetails);
    console.log('currentRegion:', currentRegion);
    console.log('previousRegion:', previousRegion);

    // Only update routes if NOT returning from details page of the same region
    if (!isReturningFromDetails || currentRegion !== previousRegion) {
      if (currentRoute && currentRoute !== this.globals.routeStr) {
        this.globals.routeStr = currentRoute;
      }
    }

    this.setGlobalSearchMin();
    this.handleDistrictDefaults();

    // Check if we need to reload listings
    const shouldReload =
      this.globals.listings.length === 0 ||
      currentRegion !== previousRegion ||
      !isReturningFromDetails;

    console.log('shouldReload:', shouldReload);

    if (shouldReload) {
      this.fetchListings();
    } else {
      this.listings = this.globals.listings;
      if (this.globals.selectedListing) {
        this.selectedListing = this.globals.selectedListing;
        this.isSelectedBln = true;
      }
    }

    this.globals.isInitialLoad = false;
    this.globals.isRegisteredBln = '1';
    this.isRegisteredBln = '1';
  }

  protected override onListingsFetched(listings: Listing[]): void {
    // Map listings to include banner property
    this.listings = listings.map((listing) => {
      const banner = this.getBannerClass(listing); // Compute banner object
      //console.log('Banner for listing:', listing.id, banner); // Debug banner logic
      return {
        ...listing,   // Include all the original properties
        banner,       // Add the banner property
      };
    });

    // Assign the updated list to the global variable
    this.globals.listings = this.listings;

    // Debugging all mutated listings
    //console.log('Listings fetched with banners:', this.listings);

    // Navigate to the first listing for desktop only (avoid during SSR/mobile)
    const isDesktop = this.currentScreenSize === 'Large' || this.currentScreenSize === 'XLarge';
    if (isDesktop && this.listings && this.listings.length > 0 && !this.isSelectedBln) {
      this.gotoDetail(this.listings[0]);
    } else if (!this.listings || this.listings.length === 0) {
      console.warn('No listings found.');
    }
  }

  protected getBannerClass(listing: Listing): { bannerClass: string, lineClass: string, text: string } | null {
    //console.log('Banner for listing:', listing.id);
    const listingStatus = (listing.listingStatus || '').toLowerCase();
    const bannerOverride = listing.listingDetail?.bannerOverride || '';

    if (listingStatus === 'sold' && !listing.exclusive) {
      return {bannerClass: 'sold', lineClass: 'line', text: '<span>Sold</span>'};
    }

    if (listing.exclusive && listingStatus !== 'sold') {
      return {bannerClass: 'exclusive', lineClass: 'line', text: '<span>Exclusive</span>'};
    }

    if (listing.exclusive && listingStatus === 'sold') {
      const lineClass = this.currentScreenSize !== 'XLarge' ? 'line-small' : 'line';

      return {
        bannerClass: 'banner-exclusive-sold sold',
        lineClass: lineClass,
        text: '<span>Sold - Exclusively listed<br/> by Cayman Marshall</span>',
      };
    }

    if (listing.isModified && !listing.isNew && !listing.listingDetail?.priceChange && listingStatus !== 'sold' && bannerOverride === '') {
      return {bannerClass: 'change', lineClass: 'line', text: '<span>Modified</span>'};
    }

    if (listing.isModified && !listing.isNew && listing.listingDetail?.priceChange && bannerOverride === '') {
      return {bannerClass: 'change', lineClass: 'line', text: '<span>Price change</span>'};
    }

    if (listing.isNew && listingStatus !== 'sold' && bannerOverride === '') {
      return {bannerClass: 'new', lineClass: 'line', text: '<span>New</span>'};
    }

    if ((listingStatus === 'rental' || listingStatus === 'active') && bannerOverride !== '' && bannerOverride !== 'n/a') {
      const isLineSmall = bannerOverride.length > 15; // Use `line-small` for longer texts
      return {bannerClass: 'rental', lineClass: isLineSmall ? 'line-small' : 'line', text: '<span>' + bannerOverride + '</span>'};
    }

    return null; // No banner
  }
}
